import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { LanguageProvider } from './contexts/LanguageContext';
import './App.css';
import './styles/responsive.css';
import './styles/layout-manager.css';
import './styles/android-fixes.css';

// Lazy load routes for better initial load performance
const Login = lazy(() => import('./components/Login'));
const WhiteboardLanding = lazy(() => import('./components/WhiteboardLanding'));
const Whiteboard = lazy(() => import('./components/Whiteboard'));
const TeacherDashboard = lazy(() => import('./components/TeacherDashboard'));
const StudentView = lazy(() => import('./components/StudentView'));
const CurriculumGenerator = lazy(() => import('./components/CurriculumGenerator'));
const QuizMode = lazy(() => import('./components/QuizMode'));
const ZAnatomy = lazy(() => import('./components/ZAnatomy'));
const Anatomy2 = lazy(() => import('./components/Anatomy2'));
const PhETSimulations = lazy(() => import('./components/PhETSimulations'));
const EffectualPhysics = lazy(() => import('./components/EffectualPhysics'));
const PDFUploadPortal = lazy(() => import('./components/PDFUploadPortal'));
const ContentManager = lazy(() => import('./components/ContentManager'));

// Loading component for route transitions
const RouteLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    color: '#667eea',
    fontSize: '18px'
  }}>
    Loading...
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [loadedSession, setLoadedSession] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const authStatus = localStorage.getItem('isAuthenticated') === 'true';
    const userInfo = localStorage.getItem('userInfo');

    if (authStatus && userInfo) {
      setUser(JSON.parse(userInfo));
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (userInfo) => {
    setUser(userInfo);
    setIsAuthenticated(true);
    setShowWhiteboard(false); // Show landing page first
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userInfo');
    setUser(null);
    setIsAuthenticated(false);
    setShowWhiteboard(false);
    setLoadedSession(null);
  };

  const handleCreateNew = () => {
    // Clear localStorage to start with a fresh canvas
    localStorage.removeItem('whiteboardCanvasState');
    localStorage.removeItem('whiteboardViewport');
    localStorage.removeItem('whiteboardZoom');
    setLoadedSession(null);
    setShowWhiteboard(true);
  };

  const handleSelectSession = (canvasData, session) => {
    setLoadedSession({ canvasData, session });
    setShowWhiteboard(true);
  };

  return (
    <LanguageProvider>
      <Router>
        <div className="App">
        <Toaster 
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#4caf50',
              },
            },
            error: {
              style: {
                background: '#f44336',
              },
            },
          }}
        />
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/" element={
              <ProtectedRoute>
                {showWhiteboard ? (
                  <Whiteboard
                    user={user}
                    onLogout={handleLogout}
                    initialSession={loadedSession}
                    onBackToLibrary={() => setShowWhiteboard(false)}
                  />
                ) : (
                  <WhiteboardLanding
                    user={user}
                    onSelectSession={handleSelectSession}
                    onCreateNew={handleCreateNew}
                    onLogout={handleLogout}
                  />
                )}
              </ProtectedRoute>
            } />
            <Route path="/teacher" element={
              <ProtectedRoute>
                <TeacherDashboard user={user} />
              </ProtectedRoute>
            } />
            <Route path="/student/:roomId" element={
              <ProtectedRoute>
                <StudentView user={user} />
              </ProtectedRoute>
            } />
            <Route path="/curriculum" element={
              <ProtectedRoute>
                <CurriculumGenerator user={user} />
              </ProtectedRoute>
            } />
            <Route path="/quiz" element={
              <ProtectedRoute>
                <QuizMode user={user} />
              </ProtectedRoute>
            } />
            <Route path="/anatomy" element={
              <ProtectedRoute>
                <ZAnatomy user={user} />
              </ProtectedRoute>
            } />
            <Route path="/anatomy2" element={
              <ProtectedRoute>
                <Anatomy2 user={user} />
              </ProtectedRoute>
            } />
            <Route path="/simulations" element={
              <ProtectedRoute>
                <PhETSimulations user={user} />
              </ProtectedRoute>
            } />
            <Route path="/physics" element={
              <ProtectedRoute>
                <EffectualPhysics user={user} />
              </ProtectedRoute>
            } />
            <Route path="/pdfupload" element={<PDFUploadPortal />} />
            <Route path="/content" element={<ContentManager />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
    </LanguageProvider>
  );
}

export default App;